import type { Board } from "../board";
import { PRIORITY_DEFAULT, type ProcessableThing, isRemoteDoor } from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import { randomElement, randomInt } from "../random";
import { deserializeXY } from "../serialization";
import { Strings } from "../strings/strings";
import type { XY } from "../xy";
import { SlidingDoor, SlidingDoorState } from "./slidingDoor";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { Terminal, TerminalState } from "./terminal";

const PROMPT_RADIUS = 1;
const RELEASE_DELAY_MIN_TURNS = 8;
export const RELEASE_DELAY_MAX_TURNS = 13;

export const ZOO_ENTRANCE_DOOR_GROUP = "zooEntrance";
export const ZOO_CELL_DOOR_GROUP = "cell";
export const ZOO_EXIT_DOOR_GROUP = "zooExit";

/**
 * Opens the whole zoo, one cell at a time: interaction opens the zooEntrance
 * doors, then every RELEASE_DELAY_MIN..MAX turns one random closed "cell" door
 * opens (plus any touching door — double doors). When no cell doors remain,
 * the zooExit doors open and the terminal goes dormant.
 */
export class ZooReleaseTerminal extends Terminal implements ProcessableThing {
	static key = "ZooReleaseTerminal";
	private countdown = 0;
	private done = false;

	static override initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
		const terminal = new ZooReleaseTerminal(xy);
		board.putThing(terminal);
		return [terminal];
	}

	processTurn(board: Board): void {
		if (this.canInteract()) {
			const player = board.player;
			if (player && this.xy.isInSquareRadius(player.xy, PROMPT_RADIUS)) {
				board.emitPrompt(Strings.get("prompt_useTerminal") ?? "prompt_useTerminal");
			}
			return;
		}
		if (this.done) return;
		this.countdown--;
		if (this.countdown > 0) return;
		this.releaseNext();
		this.countdown = randomInt(RELEASE_DELAY_MIN_TURNS, RELEASE_DELAY_MAX_TURNS);
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	runTerminalAction(): void {
		this.openDoorGroup(ZOO_ENTRANCE_DOOR_GROUP);
		this.countdown = randomInt(RELEASE_DELAY_MIN_TURNS, RELEASE_DELAY_MAX_TURNS);
		this.board!.addNonPhysicalThing(new Sound(SoundId.TERMINAL_CONFIRM, this.xy));
	}

	private releaseNext(): void {
		const closedCellDoors = this.getClosedCellDoors();
		if (closedCellDoors.length === 0) {
			this.openDoorGroup(ZOO_EXIT_DOOR_GROUP);
			this.done = true;
			return;
		}
		const door = randomElement(closedCellDoors)!;
		const doorXY = door.xy;
		door.open();
		for (const partner of closedCellDoors) {
			if (partner === door) continue;
			if (doorXY.getCardinalNeighbors().some((n) => n.equals(partner.xy))) partner.open();
		}
	}

	private getClosedCellDoors(): SlidingDoor[] {
		return this.board!.getPhysicalThings().filter(
			(t): t is SlidingDoor =>
				t instanceof SlidingDoor && t.doorId === ZOO_CELL_DOOR_GROUP && t.getState() === SlidingDoorState.CLOSED,
		);
	}

	private openDoorGroup(doorId: string): void {
		for (const thing of this.board!.getPhysicalThings()) {
			if (isRemoteDoor(thing) && thing.doorId === doorId) thing.open();
		}
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			state: this.state,
			countdown: this.countdown,
			done: this.done,
		};
	}

	static deserialize(data: any): ZooReleaseTerminal {
		const t = new ZooReleaseTerminal(deserializeXY(data.xy));
		t.visualId = data.visualId;
		t.state = data.state ?? TerminalState.ACTIVE;
		t.countdown = data.countdown ?? 0;
		t.done = data.done ?? false;
		return t;
	}
}
